import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Smartphone, Stethoscope, User, Users, X } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';
import { hasEmoji } from '@/shared/utils/emoji';

import { useDiseaseGroupsQuery, useDiseaseCodesQuery } from '../hooks/use-diagnosis-options';
import { useRegisterPatient } from '../hooks/use-patient-mutations';
import type { StaffPickerCandidate } from '../hooks/use-staff-picker';
import {
  PatientApiError,
  type PatientAppUserType,
  type PatientGender,
  type PatientGuardianRelationship,
} from '../types/patient.types';
import { PatientRegistrationStaffPicker } from './PatientRegistrationStaffPicker';

interface PatientRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  // [US-PM-07] The patient was created but the app-install SMS did not go out. Raised to the page
  // rather than handled here because this modal unmounts on close and the failure popup has to
  // outlive it — the registration itself succeeded, so the form must not stay open behind it.
  onSmsFailure?: (info: { patientId: number; target: string }) => void;
}

interface AssignedStaff {
  id: number;
  name: string;
  department: string;
  jobRole: string;
  isPrimary: boolean;
}

interface FormState {
  appUserType: PatientAppUserType;
  guardian: { name: string; relationship: PatientGuardianRelationship | ''; phone: string };
  name: string;
  birthDate: string;
  gender: PatientGender | '';
  phone: string;
  regNo: string;
  dischargeDate: string;
  mainDiagnosisGroupId: string;   // disease GROUP id (주진단); string because it's a <select> value
  subDiagnosisCodeId: string;     // disease CODE id (부진단, optional, within the chosen group)
  medication: string;
  medicationFrequency: string;
}

type FieldErrors = Partial<Record<string, string>>;

// Canonical (PRD 21 §3.3.1): exactly 11 digits, '010'-prefixed. Tested on the
// digits-only value so hyphenated input is still accepted.
const PHONE_REGEX = /^010\d{8}$/;
// SHCT-170 — validation aligned to [공통] 00 입력검증표준 (확정 2026-07-10), which
// overrides the older CR text per requirement precedence.
// 환자명 (§2.5): 한글 2~20자.
const PATIENT_NAME_REGEX = /^[가-힣]{2,20}$/;
// 보호자명 (§8 부록 → §3 NAME 클래스): 1~50자, 기본·일반특수문자 허용, 이모지·줄바꿈 불허.
const GUARDIAN_NAME_MIN = 1;
const GUARDIAN_NAME_MAX = 50;
// §4.2 — 이모지는 전 클래스 불허. Canonical helper: `@/shared/utils/emoji`.
// MRN (§2.7, 확정 2026-07-10): 영문·숫자·'-' 최대 20자. Duplicate check (BE/mock) is case-sensitive.
const MRN_REGEX = /^[A-Za-z0-9-]{1,20}$/;
// 처방약 / 복약 주기 (SHCT-170): PRD 02 defines no length of its own and defers to the
// canonical standard, which assigns both fields the NAME class — [공통] 00 §3 / §8 부록
// "처방약·복약주기 → NAME 클래스 적용": 1~50자, single-line, 일반 특수문자 허용, 이모지 불허.
// The CR text's 64 predates that standard (2026-07-10) and loses to it by requirement precedence.
const TEXT_MAX = 50;
// Relationship enum from the contract / types.
const GUARDIAN_RELATIONSHIPS: readonly PatientGuardianRelationship[] = [
  'SPOUSE',
  'CHILD',
  'PARENT',
  'SIBLING',
  'OTHER',
] as const;

const EMPTY_FORM: FormState = {
  appUserType: 'patient',
  guardian: { name: '', relationship: '', phone: '' },
  name: '',
  birthDate: '',
  gender: '',
  phone: '',
  regNo: '',
  dischargeDate: '',
  mainDiagnosisGroupId: '',
  subDiagnosisCodeId: '',
  medication: '',
  medicationFrequency: '',
};

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// PRD §3.7 — Root-only New-Patient Registration modal (720 px wide per
// prototype). Sections: app user type → guardian (conditional) → patient info
// → staff assignment. On submit: success toast → 400 ms → SMS info toast →
// close + list refresh (handled by the mutation hook).
export function PatientRegistrationModal({
  open,
  onClose,
  onSmsFailure,
}: PatientRegistrationModalProps) {
  const { t } = useTranslation('admin');

  const { data: diseaseGroups } = useDiseaseGroupsQuery();

  const registerMutation = useRegisterPatient();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [assigned, setAssigned] = useState<AssignedStaff[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [showStaffPicker, setShowStaffPicker] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // 부진단 (secondary) codes depend on the selected 주진단 (primary) group.
  const selectedGroupId = form.mainDiagnosisGroupId ? Number(form.mainDiagnosisGroupId) : null;
  const { data: diseaseCodes } = useDiseaseCodesQuery(selectedGroupId);

  // Has the user edited anything? Used to gate the discard confirm. Compared
  // against a "fresh open" baseline (empty form + no assigned staff).
  const isDirty = useMemo(() => {
    if (JSON.stringify(form) !== JSON.stringify(EMPTY_FORM)) return true;
    return assigned.length > 0;
  }, [form, assigned]);

  // Reset everything when the modal opens. [ADM.030] The caller is NOT
  // auto-assigned as Primary any more — the operator picks every physician from
  // the search modal, and the first one picked becomes 주담당의.
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setShowStaffPicker(false);
    setShowDiscard(false);
    setAssigned([]);
  }, [open]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // SHCT-794 — switching the mode changes whether 환자 휴대폰 is required, so a phone error raised
  // under the old mode no longer describes the form. `update` only clears the key it writes, which
  // would leave "휴대폰을 입력해주세요" pinned under a field that just became optional.
  const selectAppUserType = (value: PatientAppUserType) => {
    update('appUserType', value);
    if (errors.phone) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phone;
        return next;
      });
    }
  };

  const updateGuardian = <K extends keyof FormState['guardian']>(
    key: K,
    value: FormState['guardian'][K],
  ) => {
    setForm((f) => ({ ...f, guardian: { ...f.guardian, [key]: value } }));
    const errorKey = `guardian.${key}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const next: FieldErrors = {};
    const name = form.name.trim();
    if (!name) {
      next.name = t('patientManagement.registrationModal.errors.name');
    } else if (!PATIENT_NAME_REGEX.test(name)) {
      next.name = t('patientManagement.registrationModal.errors.nameLength');
    }
    // DoB & gender are entered only in guardian (proxy) mode; in self mode they
    // arrive via the patient app's PASS authentication, so they're skipped here.
    // (SHCT-299) The future-date guard (SHCT-170) is kept inside the guardian block.
    if (form.appUserType === 'guardian') {
      if (!form.birthDate) {
        next.birthDate = t('patientManagement.registrationModal.errors.birthDate');
      } else if (form.birthDate > todayIso()) {
        next.birthDate = t('patientManagement.registrationModal.errors.birthDateFuture');
      }
      if (!form.gender) {
        next.gender = t('patientManagement.registrationModal.errors.gender');
      }
    }

    // SHCT-794 — PRD 02 §3.7 조건 분기: 본인(환자) 모드에서만 환자 휴대폰이 필수다.
    // *"IF 보호자(대리) 선택 → ... 환자 휴대폰은 선택 입력"* — 대리 등록되는 환자는 본인 번호를
    // 갖고 있지 않을 수 있고, 앱 인증과 안내 SMS 는 보호자 휴대폰이 맡는다(§3.7 「발송 대상」).
    // 여기서 두 모드 모두 필수로 검사하는 바람에, 서버·프로토타입이 허용하는 등록을 어드민
    // 화면에서만 할 수 없었다 — `PatientRegistrationService` 는 보호자 모드의 빈 값을 null 로
    // 저장하고, 프로토타입도 라벨을 「선택 · 환자 보유 시」로 바꾼다.
    // 비어 있으면 통과시키되, 값이 있으면 형식은 canonical([공통] 00 §2.1)을 지켜야 한다.
    const phoneDigits = normalizePhone(form.phone);
    if (form.appUserType === 'patient') {
      if (!phoneDigits || !PHONE_REGEX.test(phoneDigits)) {
        next.phone = t('patientManagement.registrationModal.errors.phone');
      }
    } else if (phoneDigits && !PHONE_REGEX.test(phoneDigits)) {
      next.phone = t('patientManagement.registrationModal.errors.phoneOptionalFormat');
    }

    const regNo = form.regNo.trim();
    if (!regNo) {
      next.regNo = t('patientManagement.registrationModal.errors.regNo');
    } else if (!MRN_REGEX.test(regNo)) {
      next.regNo = t('patientManagement.registrationModal.errors.regNoInvalid');
    }
    if (form.medication.trim().length > TEXT_MAX) {
      next.medication = t('patientManagement.registrationModal.errors.medicationLength');
    }
    if (form.medicationFrequency.trim().length > TEXT_MAX) {
      next.medicationFrequency = t(
        'patientManagement.registrationModal.errors.medicationFrequencyLength',
      );
    }
    // SHCT-534 — discharge date may be today or in the past (comtor-confirmed;
    // PRD §3 "reject if AFTER today"). Only a future date is invalid.
    if (!form.dischargeDate) {
      next.dischargeDate = t('patientManagement.registrationModal.errors.dischargeDate');
    } else if (form.dischargeDate > todayIso()) {
      next.dischargeDate = t('patientManagement.registrationModal.errors.dischargeDateFuture');
    }
    if (!form.mainDiagnosisGroupId) {
      next.mainDiagnosisGroupId = t(
        'patientManagement.registrationModal.errors.primaryDiagnosis',
      );
    }
    // [ADM.030] 주담당의 선택 필수 — mirrors the prototype's `!assigned.some(s => s.isPrimary)`
    // guard. PRD §3.7 조건분기 words this as a toast; shown inline here so it matches every
    // other field in this modal and the Validation-Error rule in CLAUDE.md §7.1.
    if (!assigned.some((s) => s.isPrimary)) {
      next.staff = t('patientManagement.registrationModal.errors.staff');
    }

    if (form.appUserType === 'guardian') {
      const guardianName = form.guardian.name.trim();
      if (!guardianName) {
        next['guardian.name'] = t(
          'patientManagement.registrationModal.errors.guardianName',
        );
      } else if (
        guardianName.length < GUARDIAN_NAME_MIN ||
        guardianName.length > GUARDIAN_NAME_MAX
      ) {
        next['guardian.name'] = t(
          'patientManagement.registrationModal.errors.guardianNameLength',
        );
      } else if (hasEmoji(guardianName) || /[\r\n]/.test(guardianName)) {
        // NAME class allows 한글·영문·숫자·공백·일반특수문자; only 이모지·줄바꿈 are blocked (§4.2).
        next['guardian.name'] = t(
          'patientManagement.registrationModal.errors.guardianNameInvalid',
        );
      }
      if (!form.guardian.relationship) {
        next['guardian.relationship'] = t(
          'patientManagement.registrationModal.errors.guardianRelationship',
        );
      }
      const guardianDigits = normalizePhone(form.guardian.phone);
      if (!guardianDigits || !PHONE_REGEX.test(guardianDigits)) {
        next['guardian.phone'] = t(
          'patientManagement.registrationModal.errors.guardianPhone',
        );
      } else if (guardianDigits === phoneDigits) {
        // SHCT-536 (PAT.006) — one number must never be linked to both the patient and the
        // guardian: NICE identity verification ties a number to a single owner, so one of the
        // two would fail app sign-up. Compared on the normalized digits so hyphenated input
        // still collides. Reuses the patient-duplicate copy ("이미 사용 중인 휴대전화 번호입니다.")
        // — the guardian field is the offender against the already-entered patient number.
        // Cross-record clashes (guardian number already used by another patient, and vice
        // versa) are a server-side lookup — see the BE note on SHCT-536.
        next['guardian.phone'] = t(
          'patientManagement.registrationModal.errors.phoneDuplicate',
        );
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    if (registerMutation.isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (registerMutation.isPending) return;
    if (!validate()) return;

    // [ADM.030] ids[0] is the 주담당의 — the BE writes CareTeam with `isPrimary = (i == 0)`,
    // so the chip order (first picked stays first) IS the primary assignment.
    const assignedStaffIds = assigned.map((s) => s.id);

    // Diagnosis: 주진단 = the selected disease GROUP id; 부진단 = the optional disease CODE id within it
    // (admin prototype 02 환자관리). Select values are strings → parse to the numeric ids the BE expects.
    const mainDiagnosisGroupId = Number(form.mainDiagnosisGroupId);
    const subDiagnosisCodeId = form.subDiagnosisCodeId ? Number(form.subDiagnosisCodeId) : null;

    const guardianMode = form.appUserType === 'guardian';
    try {
      const created = await registerMutation.mutateAsync({
        appUserType: form.appUserType,
        name: form.name.trim(),
        birthDate: form.birthDate,
        gender: form.gender as PatientGender,
        phone: form.phone.trim(),
        mrn: form.regNo.trim(),
        dischargeDate: form.dischargeDate,
        mainDiagnosisGroupId,
        subDiagnosisCodeId,
        medicationCycle: form.medicationFrequency.trim() || null,
        prescriptionDrug: form.medication.trim() || null,
        guardianName: guardianMode ? form.guardian.name.trim() : undefined,
        guardianRelationship: guardianMode
          ? (form.guardian.relationship as PatientGuardianRelationship)
          : undefined,
        guardianPhone: guardianMode ? form.guardian.phone.trim() : undefined,
        assignedStaffIds,
      });

      toast.success(
        t('patientManagement.toasts.registerSuccess', { name: created.maskedName }),
      );
      // PRD §3.7: 400 ms after the success toast, surface the SMS info toast. [US-PM-07] The 발송
      // 안내 토스트 is a claim about the gateway, so it is now conditional — when the server says the
      // send failed, PRD §3.7 등록 안내 SMS calls for a failure MODAL carrying the reason and a retry,
      // and asserting "발송했습니다" first would contradict the popup opening behind it.
      const smsTarget =
        form.appUserType === 'guardian'
          ? t('patientManagement.toasts.smsTargetGuardian', {
              name: form.guardian.name.trim(),
            })
          : created.maskedName;
      if (created.smsSent === false) {
        onSmsFailure?.({ patientId: created.id, target: smsTarget });
      } else {
        window.setTimeout(() => {
          toast.info(t('patientManagement.toasts.smsDispatched', { target: smsTarget }));
        }, 400);
      }

      onClose();
    } catch (e) {
      if (e instanceof PatientApiError) {
        // Duplicate phone (patient / guardian) — map the CODE to the locale-aware message, inline on
        // the matching field. Wording is customer-confirmed (Confluence Q70), kept distinct from the
        // generic phoneRejected used elsewhere; mapping the code (not the server text) makes it KO/EN.
        if (e.code === 'PATIENT_MOBILE_DUPLICATE') {
          setErrors((prev) => ({
            ...prev,
            phone: t('patientManagement.registrationModal.errors.phoneDuplicate'),
          }));
          return;
        }
        if (e.code === 'GUARDIAN_MOBILE_DUPLICATE') {
          // The guardian-phone field reads errors['guardian.phone'] (namespaced), NOT errors.guardianPhone.
          setErrors((prev) => ({
            ...prev,
            'guardian.phone': t('patientManagement.registrationModal.errors.guardianPhoneDuplicate'),
          }));
          return;
        }
        // [ADM.030] server-side backstop for the required-primary rule (the FE blocks it in
        // validate(), so this only fires on a stale client or a direct API call).
        // PATIENT_STAFF_DUPLICATE is deliberately NOT mapped here — the picker already hides
        // assigned staff so it is unreachable from the UI, and no approved copy exists for it;
        // it falls through to the server message below.
        if (e.code === 'PATIENT_PRIMARY_PHYSICIAN_REQUIRED') {
          setErrors((prev) => ({
            ...prev,
            staff: t('patientManagement.registrationModal.errors.staff'),
          }));
          return;
        }
        if (e.fields) {
          setErrors(e.fields as FieldErrors);
          return;
        }
        toast.error(e.message);
        return;
      }
      toast.error(t('patientManagement.toasts.registerError'));
    }
  };

  const handleStaffSelect = (staff: StaffPickerCandidate) => {
    setAssigned((prev) => [
      ...prev,
      {
        id: staff.id,
        name: staff.name,
        department: staff.department,
        jobRole: staff.jobRole,
        // [ADM.030] the first physician added becomes 주담당의.
        isPrimary: prev.length === 0,
      },
    ]);
    // Picking a physician clears the "primary required" error.
    setErrors((prev) => {
      if (!prev.staff) return prev;
      const next = { ...prev };
      delete next.staff;
      return next;
    });
    setShowStaffPicker(false);
  };

  // [ADM.030] Every chip is removable, including 주담당. When the primary is removed the
  // next remaining physician is promoted (prototype patients.html L617).
  const removeAssigned = (id: number) => {
    setAssigned((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (next.length && !next.some((s) => s.isPrimary)) {
        next[0] = { ...next[0], isPrimary: true };
      }
      return next;
    });
  };

  const assignedIdsForExclude = assigned.map((s) => s.id);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="modal-overlay modal-overlay--visible"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal"
          style={{
            maxWidth: 720,
            width: '100%',
            maxHeight: '90vh',
            // Modal becomes a flex column so the body can scroll between a
            // fixed header and footer. The global `.modal` class has neither
            // display:flex nor max-height, so these have to live inline.
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal__header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <h2 className="modal__title">
              {t('patientManagement.registrationModal.title')}
            </h2>
            <button
              type="button"
              className="modal__close"
              onClick={handleClose}
              aria-label={t('patientManagement.actions.cancel')}
              style={{
                background: 'none',
                border: 'none',
                padding: 6,
                cursor: 'pointer',
                color: 'var(--muted-foreground)',
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* `minHeight: 0` is required for the inner overflow to take effect
              inside a flex column — without it the body would still try to
              grow to its content size and push the footer off-screen. */}
          <div
            className="modal__body"
            style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}
          >
            {/* App User Type */}
            <SectionTitle
              icon={<Smartphone size={16} />}
              label={t('patientManagement.registrationModal.sections.appUserType')}
              required
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-5)',
              }}
            >
              {(['patient', 'guardian'] as const).map((value) => (
                <label key={value} style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="appUserType"
                    value={value}
                    checked={form.appUserType === value}
                    onChange={() => selectAppUserType(value)}
                    style={{ display: 'none' }}
                  />
                  <div
                    style={{
                      border: `2px solid ${form.appUserType === value ? 'var(--primary)' : 'var(--border)'}`,
                      background: form.appUserType === value ? '#eff6ff' : undefined,
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3) var(--space-4)',
                      transition: 'all .15s',
                    }}
                  >
                    <strong style={{ display: 'block', fontSize: 'var(--font-size-sm)' }}>
                      {t(`patientManagement.registrationModal.appUserType.${value}.title`)}
                    </strong>
                    <small style={{ color: 'var(--muted-foreground)', fontSize: 'var(--font-size-xs)' }}>
                      {t(`patientManagement.registrationModal.appUserType.${value}.subtitle`)}
                    </small>
                  </div>
                </label>
              ))}
            </div>

            {/* Guardian (conditional) */}
            {form.appUserType === 'guardian' && (
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-5)',
                }}
              >
                <SectionTitle
                  icon={<Users size={16} />}
                  label={t('patientManagement.registrationModal.sections.guardian')}
                />
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--muted-foreground)',
                    margin: '0 0 var(--space-3)',
                    lineHeight: 1.6,
                  }}
                >
                  {t('patientManagement.registrationModal.guardian.note')}
                </p>
                <FormGrid>
                  <FormField
                    label={t('patientManagement.registrationModal.guardian.name')}
                    required
                    error={errors['guardian.name']}
                  >
                    <input
                      type="text"
                      className={`form-field__input${errors['guardian.name'] ? ' form-field__input--error' : ''}`}
                      placeholder={t('patientManagement.registrationModal.guardian.namePlaceholder')}
                      maxLength={50}
                      value={form.guardian.name}
                      onChange={(e) => updateGuardian('name', e.target.value)}
                    />
                  </FormField>
                  <FormField
                    label={t('patientManagement.registrationModal.guardian.relationship')}
                    required
                    error={errors['guardian.relationship']}
                  >
                    <select
                      className={`form-field__select${errors['guardian.relationship'] ? ' form-field__select--error' : ''}`}
                      value={form.guardian.relationship}
                      onChange={(e) =>
                        updateGuardian(
                          'relationship',
                          e.target.value as PatientGuardianRelationship,
                        )
                      }
                    >
                      <option value="" disabled hidden>
                        {t('patientManagement.registrationModal.guardian.relationshipPlaceholder')}
                      </option>
                      {GUARDIAN_RELATIONSHIPS.map((r) => (
                        <option key={r} value={r}>
                          {t(`patientManagement.guardianRelationships.${r}`)}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField
                    fullWidth
                    label={t('patientManagement.registrationModal.guardian.phone')}
                    required
                    hint={t('patientManagement.registrationModal.guardian.phoneHint')}
                    error={errors['guardian.phone']}
                  >
                    <input
                      type="tel"
                      className={`form-field__input${errors['guardian.phone'] ? ' form-field__input--error' : ''}`}
                      placeholder="01012345678"
                      inputMode="numeric"
                      value={form.guardian.phone}
                      onChange={(e) => updateGuardian('phone', toPhoneInputValue(e.target.value))}
                    />
                  </FormField>
                </FormGrid>
              </div>
            )}

            {/* Patient Info */}
            <SectionTitle
              icon={<User size={16} />}
              label={t('patientManagement.registrationModal.sections.patient')}
            />
            {/* Self mode: DoB & gender arrive via the patient app's PASS auth, so
                they aren't entered here. */}
            {form.appUserType === 'patient' && (
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--muted-foreground)',
                  margin: '0 0 var(--space-4)',
                  lineHeight: 1.6,
                }}
              >
                {t('patientManagement.registrationModal.patient.selfModeNote')}
              </p>
            )}
            <FormGrid>
              <FormField
                label={t('patientManagement.registrationModal.patient.name')}
                required
                error={errors.name}
              >
                <input
                  type="text"
                  className={`form-field__input${errors.name ? ' form-field__input--error' : ''}`}
                  maxLength={20}
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                />
              </FormField>
              {form.appUserType === 'guardian' && (
                <>
                  <FormField
                    label={t('patientManagement.registrationModal.patient.birthDate')}
                    required
                    error={errors.birthDate}
                  >
                    <input
                      type="date"
                      className={`form-field__input${errors.birthDate ? ' form-field__input--error' : ''}`}
                      max={todayIso()}
                      value={form.birthDate}
                      onChange={(e) => update('birthDate', e.target.value)}
                    />
                  </FormField>
                  <FormField
                    label={t('patientManagement.registrationModal.patient.gender')}
                    required
                    error={errors.gender}
                  >
                    <select
                      className={`form-field__select${errors.gender ? ' form-field__select--error' : ''}`}
                      value={form.gender}
                      onChange={(e) => update('gender', e.target.value as PatientGender)}
                    >
                      <option value="" disabled hidden>
                        {t('patientManagement.registrationModal.patient.genderPlaceholder')}
                      </option>
                      <option value="M">{t('patientManagement.genders.M')}</option>
                      <option value="F">{t('patientManagement.genders.F')}</option>
                    </select>
                  </FormField>
                </>
              )}
              <FormField
                label={
                  form.appUserType === 'patient'
                    ? t('patientManagement.registrationModal.patient.phoneSelf')
                    : t('patientManagement.registrationModal.patient.phoneGuardian')
                }
                required={form.appUserType === 'patient'}
                hint={t(
                  form.appUserType === 'patient'
                    ? 'patientManagement.registrationModal.patient.phoneHint'
                    : 'patientManagement.registrationModal.patient.phoneHintGuardian',
                )}
                error={errors.phone}
              >
                <input
                  type="tel"
                  className={`form-field__input${errors.phone ? ' form-field__input--error' : ''}`}
                  placeholder="01012345678"
                  inputMode="numeric"
                  value={form.phone}
                  onChange={(e) => update('phone', toPhoneInputValue(e.target.value))}
                />
              </FormField>
              <FormField
                label={t('patientManagement.registrationModal.patient.regNo')}
                required
                error={errors.regNo}
              >
                <input
                  type="text"
                  className={`form-field__input${errors.regNo ? ' form-field__input--error' : ''}`}
                  placeholder="2026-0001"
                  maxLength={20}
                  value={form.regNo}
                  onChange={(e) => update('regNo', e.target.value)}
                />
              </FormField>
              <FormField
                label={t('patientManagement.registrationModal.patient.dischargeDate')}
                required
                hint={t('patientManagement.registrationModal.patient.dischargeDateHint')}
                error={errors.dischargeDate}
                highlight
              >
                <input
                  type="date"
                  className={`form-field__input${errors.dischargeDate ? ' form-field__input--error' : ''}`}
                  // PRO reference date — highlighted light-yellow per prototype
                  // (the error border takes precedence when invalid).
                  style={
                    errors.dischargeDate
                      ? undefined
                      : { borderColor: '#fde047', backgroundColor: '#fefce8' }
                  }
                  max={todayIso()}
                  value={form.dischargeDate}
                  onChange={(e) => update('dischargeDate', e.target.value)}
                />
              </FormField>
              <FormField
                label={t('patientManagement.registrationModal.patient.primaryDiagnosis')}
                required
                error={errors.mainDiagnosisGroupId}
              >
                {/* 주진단 = disease GROUP. Changing it clears the dependent 부진단 code. */}
                <select
                  className={`form-field__select${errors.mainDiagnosisGroupId ? ' form-field__select--error' : ''}`}
                  value={form.mainDiagnosisGroupId}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      mainDiagnosisGroupId: e.target.value,
                      subDiagnosisCodeId: '',
                    }))
                  }
                >
                  <option value="" disabled hidden>
                    {t('patientManagement.registrationModal.patient.primaryDiagnosisPlaceholder')}
                  </option>
                  {(diseaseGroups ?? []).map((g) => (
                    <option key={g.id} value={String(g.id)}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('patientManagement.registrationModal.patient.secondaryDiagnosis')}
              >
                {/* 부진단 = a disease CODE within the chosen group (disabled until a 주진단 group is picked). */}
                <select
                  className="form-field__select"
                  value={form.subDiagnosisCodeId}
                  disabled={!form.mainDiagnosisGroupId}
                  onChange={(e) => update('subDiagnosisCodeId', e.target.value)}
                >
                  <option value="" disabled hidden>
                    {t('patientManagement.registrationModal.patient.secondaryDiagnosisPlaceholder')}
                  </option>
                  {(diseaseCodes ?? []).map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('patientManagement.registrationModal.patient.medication')}
                error={errors.medication}
              >
                <input
                  type="text"
                  className={`form-field__input${errors.medication ? ' form-field__input--error' : ''}`}
                  maxLength={TEXT_MAX}
                  placeholder={t('patientManagement.registrationModal.patient.medicationPlaceholder')}
                  value={form.medication}
                  onChange={(e) => update('medication', e.target.value)}
                />
              </FormField>
              <FormField
                label={t('patientManagement.registrationModal.patient.medicationFrequency')}
                error={errors.medicationFrequency}
              >
                <input
                  type="text"
                  className={`form-field__input${errors.medicationFrequency ? ' form-field__input--error' : ''}`}
                  placeholder={t(
                    'patientManagement.registrationModal.patient.medicationFrequencyPlaceholder',
                  )}
                  maxLength={TEXT_MAX}
                  value={form.medicationFrequency}
                  onChange={(e) => update('medicationFrequency', e.target.value)}
                />
              </FormField>
            </FormGrid>

            {/* Staff Assignment */}
            <div style={{ marginTop: 'var(--space-5)' }}>
              <SectionTitle
                icon={<Stethoscope size={16} />}
                label={t('patientManagement.registrationModal.sections.staff')}
              />
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2)',
                  // Matches .form-field__input--error, which uses --error for the border.
                  border: `1px solid ${errors.staff ? 'var(--error)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  minHeight: 48,
                  background: '#fff',
                }}
              >
                {assigned.map((s) => (
                  <span
                    key={s.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      background: s.isPrimary ? '#dbeafe' : '#f3f4f6',
                      color: s.isPrimary ? '#1e40af' : undefined,
                      borderRadius: 14,
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 5px',
                        borderRadius: 3,
                        background: s.isPrimary ? 'rgba(30,64,175,.15)' : 'rgba(0,0,0,.08)',
                        fontWeight: 600,
                      }}
                    >
                      {s.isPrimary
                        ? t('patientManagement.registrationModal.staff.primary')
                        : t('patientManagement.registrationModal.staff.secondary')}
                    </span>
                    <span>{s.name}</span>
                    {(s.department || s.jobRole) && (
                      <span style={{ color: 'var(--muted-foreground)', fontSize: 11 }}>
                        · {s.department}
                        {s.department && s.jobRole ? ' · ' : ''}
                        {s.jobRole}
                      </span>
                    )}
                    {/* [ADM.030] 주담당 chip is removable too — the next one is promoted. */}
                    <button
                      type="button"
                      onClick={() => removeAssigned(s.id)}
                      aria-label={t('patientManagement.actions.cancel')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: 'var(--muted-foreground)',
                        display: 'inline-flex',
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              {errors.staff && <p className="form-field__error">{errors.staff}</p>}
              <button
                type="button"
                className="btn btn-default btn-secondary"
                onClick={() => setShowStaffPicker(true)}
                style={{ marginTop: 8 }}
              >
                <Plus size={14} />{' '}
                {t('patientManagement.registrationModal.staff.add')}
              </button>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--muted-foreground)',
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                {t('patientManagement.registrationModal.staff.note')}
              </p>
            </div>
          </div>

          <div className="modal__footer" style={{ flexShrink: 0 }}>
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleClose}
              disabled={registerMutation.isPending}
            >
              {t('patientManagement.actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={handleSubmit}
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending
                ? t('patientManagement.registrationModal.submitting')
                : t('patientManagement.registrationModal.submit')}
            </button>
          </div>
        </div>
      </div>

      <PatientRegistrationStaffPicker
        open={showStaffPicker}
        excludeIds={assignedIdsForExclude}
        onSelect={handleStaffSelect}
        onClose={() => setShowStaffPicker(false)}
      />

      <ConfirmModal
        open={showDiscard}
        title={t('patientManagement.modals.discard.title')}
        message={t('patientManagement.modals.discard.message')}
        confirmLabel={t('patientManagement.modals.discard.confirm')}
        cancelLabel={t('patientManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={() => {
          setShowDiscard(false);
          onClose();
        }}
        onCancel={() => setShowDiscard(false)}
      />
    </>,
    document.body,
  );
}

// ── Local presentational helpers ─────────────────────────────────────────────

function SectionTitle({
  icon,
  label,
  required,
}: {
  icon: React.ReactNode;
  label: string;
  required?: boolean;
}) {
  return (
    <h3
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        color: 'var(--foreground)',
        marginBottom: 'var(--space-3)',
        paddingBottom: 'var(--space-2)',
        borderBottom: '1px solid var(--border)',
        marginTop: 0,
      }}
    >
      {icon}
      {label}
      {required && <span className="required">*</span>}
    </h3>
  );
}

function FormGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '14px 12px',
        alignItems: 'start',
      }}
    >
      {children}
    </div>
  );
}

function FormField({
  label,
  required,
  hint,
  error,
  fullWidth,
  highlight,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  fullWidth?: boolean;
  highlight?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="form-field"
      style={{
        marginBottom: 0,
        minWidth: 0,
        gridColumn: fullWidth ? '1 / -1' : undefined,
      }}
    >
      <label
        className="form-field__label"
        style={{
          display: 'block',
          lineHeight: 1.45,
          color: highlight ? '#854d0e' : undefined,
          fontWeight: highlight ? 700 : undefined,
        }}
      >
        {label} {required && <span className="required">*</span>}
        {hint && (
          <span style={{ color: 'var(--muted-foreground)', fontSize: 11, fontWeight: 400 }}>
            {' '}
            {hint}
          </span>
        )}
      </label>
      {children}
      {error && <p className="form-field__error">{error}</p>}
    </div>
  );
}
